import { useState, useEffect } from "react";

interface BatteryStatus {
  level: number;
  charging: boolean;
  isLow: boolean;
  isCritical: boolean;
  supported: boolean;
}

interface BatteryManager extends EventTarget {
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
  level: number;
  onchargingchange: ((this: BatteryManager, ev: Event) => void) | null;
  onchargingtimechange: ((this: BatteryManager, ev: Event) => void) | null;
  ondischargingtimechange: ((this: BatteryManager, ev: Event) => void) | null;
  onlevelchange: ((this: BatteryManager, ev: Event) => void) | null;
}

declare global {
  interface Navigator {
    getBattery?: () => Promise<BatteryManager>;
  }
}

const LOW_BATTERY_THRESHOLD = 20;
const CRITICAL_BATTERY_THRESHOLD = 10;

export const useBatteryStatus = () => {
  const [batteryStatus, setBatteryStatus] = useState<BatteryStatus>({
    level: 100,
    charging: true,
    isLow: false,
    isCritical: false,
    supported: false,
  });

  useEffect(() => {
    let battery: BatteryManager | null = null;

    const updateBatteryStatus = (b: BatteryManager) => {
      const level = Math.round(b.level * 100);
      setBatteryStatus({
        level,
        charging: b.charging,
        isLow: !b.charging && level <= LOW_BATTERY_THRESHOLD,
        isCritical: !b.charging && level <= CRITICAL_BATTERY_THRESHOLD,
        supported: true,
      });
    };

    const initBattery = async () => {
      if (!navigator.getBattery) {
        console.log("Battery Status API not supported");
        return;
      }

      try {
        battery = await navigator.getBattery();
        updateBatteryStatus(battery);

        // Listen for battery changes
        battery.addEventListener("levelchange", () => {
          if (battery) updateBatteryStatus(battery);
        });
        battery.addEventListener("chargingchange", () => {
          if (battery) updateBatteryStatus(battery);
        });
      } catch (error) {
        console.error("Error accessing battery status:", error);
      }
    };

    initBattery();

    return () => {
      // Cleanup listeners if needed
      if (battery) {
        battery.onlevelchange = null;
        battery.onchargingchange = null;
      }
    };
  }, []);

  return batteryStatus;
};
